/**
 * noteVocabulary — the FRONTEND mirror of the note-pipeline payload-shape
 * registry. Verbatim value-mirror of api/_shared/noteVocabulary.ts
 * (PROPOSAL_PAYLOAD_SPECS + the closed vocabularies it references).
 *
 * WHY A MIRROR (not an import): the frontend and api are separate
 * packages either side of a deliberate boundary — the FE tsconfig
 * excludes `api`, the FE bundle has its own deps, and api/_shared/
 * noteVocabulary.ts type-imports ./schema (the Kysely registry, an
 * api-only dependency). Reaching across that line couples the FE build to
 * api internals. So every shared contract is HAND-MIRRORED on the FE
 * side, never imported — capabilities.ts ↔ api/_shared/capabilities.ts,
 * facetTaxonomy.ts ↔ schema.ts, suggestedEdits.ts ↔ repos/suggestedEdits.ts.
 *
 * ONE registry, faithfully mirrored: the ConfirmQueue reads its typed
 * fields from here so it never hand-invents a second vocabulary (the
 * pre-B1 inline TYPE_LABELS/MED_FIELDS had drifted to types that no
 * longer exist). Widening the api registry ⇒ update this mirror in the
 * same change. The drift guard is
 * tests/ui/NoteVocabularyConformance.test.ts (registry-driven seam pin,
 * ClientRecordConformance precedent): it fails CI on any value
 * asymmetry between the two sides, in either direction.
 *
 * Value-level mirror: the api source annotates its vocab arrays with the
 * branded schema enum types (ClientMedicationStatus, …) it can import;
 * the FE has no access to those brands, so the arrays are typed
 * `readonly string[]` here. The VALUES are byte-identical; only the type
 * annotations differ (which is all the FE rendering needs). One further
 * deviation: hint strings are normalized to the FE no-em-dash house style
 * (tools/lint-no-em-dashes.sh) — the hints are prompt/help copy, not
 * rendered as brand surface here, so the wording is equivalent.
 */

// ---------------------------------------------------------------------
// The proposal-type union (mirror of schema.ts NoteProposalType — the 16
// types the pipeline emits; the medication family shares one spec, ditto
// appointments and conditions).
// ---------------------------------------------------------------------

export type NoteProposalType =
  | 'medication_add'
  | 'medication_change'
  | 'medication_discontinue'
  | 'appointment_past'
  | 'appointment_upcoming'
  | 'condition_add'
  | 'condition_change'
  | 'housing_change'
  | 'risk_change'
  | 'provider_new'
  | 'contact_change'
  | 'hospital_event'
  | 'directive_change'
  | 'coverage_change'
  | 'functional_status_change'
  | 'allergy_change';

// ---------------------------------------------------------------------
// Closed vocabularies (§1) — structural sets mirror the 0120 CHECKs
// ---------------------------------------------------------------------

export const MEDICATION_STATUSES: readonly string[] = ['active', 'discontinued'];

/**
 * When a medication is taken (client_medications.times_of_day): the
 * medication list's five blocks, in the list's order. A stored set holds
 * each at most once, in this order.
 */
export const MEDICATION_TIMES_OF_DAY: readonly string[] = [
  'morning',
  'afternoon',
  'evening',
  'bedtime',
  'as_needed',
];

export const CONDITION_STATUSES: readonly string[] = ['active', 'watch', 'resolved'];

export const RISK_LEVELS: readonly string[] = ['watch', 'elevated', 'resolved'];

export const APPOINTMENT_STATUSES: readonly string[] = [
  'scheduled',
  'attended',
  'missed',
  'cancelled',
];

/** Mirror of the 0144 client_appointments_recurrence_check (Soak #2 Wave
 *  B / G2). A recurring appointment stays ONE row (the series). */
export const APPOINTMENT_RECURRENCES: readonly string[] = [
  'none',
  'weekly',
  'biweekly',
  'monthly',
];

export const LIVING_SITUATIONS: readonly string[] = [
  'home',
  'apartment',
  'group_home',
  'independent_living',
  'assisted_living',
  'memory_care',
  'nursing_home',
  'rehab_facility',
  'hospice',
  'other',
];

/** Mirror of the 0221 clients_relationship_status_check. NULL = not
 *  recorded. */
export const RELATIONSHIP_STATUS_VALUES: readonly string[] = ['single', 'married', 'widowed', 'divorced'];


/** The living situations that are a home (a housing note's return home);
 *  in-home support no longer depends on them (0229). */
export const IN_HOME_SUPPORT_STATUSES: readonly string[] = ['home', 'apartment'];

/** Mirror of the 0225 client_addresses_place_type_check: the kind of
 *  place an address is. The ten living situations, with family, a
 *  hospital and a mailing-only address. */
export const PLACE_TYPES: readonly string[] = [
  'home',
  'apartment',
  'with_family',
  'independent_living',
  'group_home',
  'assisted_living',
  'memory_care',
  'nursing_home',
  'rehab_facility',
  'hospital',
  'hospice',
  'mailing',
  'other',
];

/** The stored label each type of place decides, so every label reader
 *  (the chip fallback, the trail, the importers) keeps reading. */
export const PLACE_TYPE_LABEL_KIND: Readonly<Record<string, string>> = {
  home: 'home',
  apartment: 'home',
  with_family: 'home',
  independent_living: 'home',
  group_home: 'facility',
  assisted_living: 'facility',
  memory_care: 'facility',
  nursing_home: 'facility',
  rehab_facility: 'facility',
  hospital: 'facility',
  hospice: 'facility',
  mailing: 'mailing',
  other: 'other',
};

/** The types of place that are a care setting: a housing note never puts
 *  one on the home address, and leaving one for home is the return home. */
export const CARE_SETTING_PLACE_TYPES: readonly string[] = [
  'group_home',
  'assisted_living',
  'memory_care',
  'nursing_home',
  'rehab_facility',
  'hospital',
  'hospice',
];

/** Mirror of the 0127 clients_transportation_consent_check (the [M3]
 *  housing pattern). NULL = not recorded; the honest third state is the
 *  ABSENCE of a value, never a vocabulary member. */
export const TRANSPORTATION_CONSENT_VALUES: readonly string[] = ['consented', 'declined'];

/** Mirror of the 0128 clients_pronouns_check (the 0127 shape). NULL =
 *  not recorded — never a vocabulary member — and NULL never gates
 *  anything (every consumer works without a value). */
export const CLIENT_PRONOUN_VALUES: readonly string[] = ['she/her', 'he/him', 'they/them'];

/** clients.gender (0137, WS-4 D1). App-validated, deliberately NO DB
 *  CHECK ([M7]/rate_band posture) — this registry is the one authority
 *  (the writer validates against it; the UI picker renders from it).
 *  NULL = not recorded, never a vocabulary member. Config-seam
 *  candidate later. */
export const CLIENT_GENDER_VALUES: readonly string[] = [
  'female',
  'male',
  'nonbinary',
  'prefer_not_to_say',
  'unknown',
];

/** clients.engagement (0137, WS-4 D3): what Peace does for this client.
 *  App-validated, no DB CHECK; NOT NULL DEFAULT care_management. */
export const CLIENT_ENGAGEMENT_VALUES: readonly string[] = ['care_management', 'spice', 'both'];

/** clients.lifecycle_status (0145, Soak #2 Wave B / G6, D2): the client's
 *  real-world lifecycle marker. DB CHECK-backed (clients_lifecycle_status_check,
 *  the 0128 pronouns posture); this mirror is what the picker renders from.
 *  'archived' is deliberately NOT a member — "Archived" derives from
 *  !is_active (the orthogonal archive axis). Byte-mirror of the BE export. */
export const CLIENT_LIFECYCLE_STATUS_VALUES: readonly string[] = [
  'active',
  'inactive_awaiting_payment',
  'deceased',
  'deceased_family_work',
];

/** The statuses a person can set: every stored status. 0212 removed 'opg'
 *  from the CHECK once every OPG client was Inactive with a status note,
 *  so the two lists are equal. Byte-mirror of the BE export. */
export const CLIENT_LIFECYCLE_STATUS_CHOICES: readonly string[] = [
  'active',
  'inactive_awaiting_payment',
  'deceased',
  'deceased_family_work',
];

/** client_addresses.label (0137, WS-4 D2). App-validated, no DB CHECK
 *  — repos/clientAddresses.ts is the one writer. */
export const CLIENT_ADDRESS_LABEL_VALUES: readonly string[] = [
  'home',
  'facility',
  'mailing',
  'other',
];

// 'will' deliberately sorts LAST (WS-8, founder 2026-07-10): the team
// rarely asks about a Will, so it stays recordable but de-emphasized —
// last in every picker derived from this order, and never part of a
// missing-items / attention prompt (no surface nags for it; keep it so).
// Validation is set-membership, so the order is display-only.
export const LEGAL_INSTRUMENTS: readonly string[] = [
  'code_status',
  'polst',
  'healthcare_proxy',
  'dpoa',
  'trust',
  'guardianship',
  'will',
];

/** The instruments that carry a code-status set. */
export const CODE_STATUS_INSTRUMENTS: readonly string[] = ['code_status', 'polst'];

export const LEGAL_STATUSES: readonly string[] = [
  'on_file',
  'in_progress',
  'recommended',
  'missing',
  'unknown',
];

export const CODE_STATUS_VALUES: readonly string[] = [
  'full',
  'dnr',
  'dni',
  // dnh (do not hospitalize) joined at 0136 (WS-3 clinical safety fields).
  'dnh',
  'comfort',
  'unknown',
];

/** The combinable code-status members (dnr/dni/dnh stack freely); the
 *  remaining vocabulary (full/comfort/unknown) are exclusive singletons.
 *  Mirror of the 0136 client_legal_instruments_code_status_values_check. */
export const CODE_STATUS_COMBINABLE: readonly string[] = ['dnr', 'dni', 'dnh'];

/** Mirror of the app-validated client_allergies.severity vocabulary
 *  (0136; NO DB CHECK by the [M7]/rate_band single-writer precedent;
 *  NULL = not stated, never a vocabulary member). */
export const ALLERGY_SEVERITIES: readonly string[] = ['mild', 'moderate', 'severe', 'unknown'];

export const HOSPITAL_KINDS: readonly string[] = [
  'er_visit',
  'admission',
  'discharge',
  'rehab_admission',
  'rehab_discharge',
];

export const COVERAGE_PROGRAMS: readonly string[] = [
  'medicare_a',
  'medicare_b',
  'medicare_a_b',
  'medicare_advantage',
  'part_d',
  'supplement',
  'medicaid',
  'ltc_insurance',
  'private',
  'other',
];

/** The programs the readers (the note reader, the insurance card reader)
 *  may say: every program but Medicare A + B, which only a person picks
 *  (a note or a card names Part A and Part B). */
export const COVERAGE_READER_PROGRAMS: readonly string[] = COVERAGE_PROGRAMS.filter((p) => p !== 'medicare_a_b');

export const COVERAGE_STATUSES: readonly string[] = ['active', 'inactive', 'pending', 'unverified'];

export const FUNCTIONAL_ACTIVITIES: readonly string[] = [
  'bathing',
  'grooming',
  'toileting',
  'dressing_upper',
  'dressing_lower',
  'transfer_chair',
  'transfer_bed',
  'ambulation',
  'feeding',
  'meal_prep',
  'housekeeping',
  'laundry',
  'medication_setup',
  'medication_reminders',
  'finances',
  'shopping',
  'telephone',
  'driving',
];

export const FUNCTIONAL_LEVELS: readonly string[] = [
  'independent',
  'needs_assistance',
  'dependent',
  'not_assessed',
];

/**
 * The seeded default risk-category set. Tenant-flavored per tenants.config
 * (config.features.risk_categories); the risk card's PR wires the
 * per-tenant read — this constant is the fallback, never a blank grid.
 */
export const DEFAULT_RISK_CATEGORIES: readonly string[] = [
  'fall',
  'medication_management',
  'cognitive_decline',
  'social_isolation',
  'caregiver_burnout',
  'home_safety',
  'financial_legal',
  'other',
];

// ---------------------------------------------------------------------
// The per-type payload registry (the pinned field names)
// ---------------------------------------------------------------------

export type PayloadFieldKind =
  | 'string' // free text
  | 'email' // an email address: free text, never capitalised, stored lower-case
  | 'date' // 'YYYY-MM-DD'; honest null welcome
  | 'time' // 'HH:MM', 24-hour wall clock in the practice's day; confirm-time and direct entry only
  | 'boolean'
  | 'enum' // closed §1 vocabulary (values below)
  | 'enum_set' // a SET drawn from a closed vocabulary (code_status_values, a medication's times_of_day)
  | 'risk_category' // the tenant-config vocabulary
  | 'postal_address' // an address as its five parts (line1, line2, city, state, zip), or a legacy string
  | 'uuid'; // row references — confirm-time only, never model-emitted

/**
 * How a free-text value is cased and shown. Name kinds (person, place,
 * role, relationship, carrier) take name case on leaving the box and on
 * the record's server writes. One-line values are stored as typed and
 * shown through displayValue in their scope (value, medication,
 * medication_reason, diagnosis, appointment_purpose, allergy). note and
 * address_text are stored and shown exactly as typed; phone is stored
 * as typed. code (an identifier on a card: a member ID, a group number,
 * an Rx BIN) is stored and shown exactly as typed, never with a first
 * capital. Every 'string' field carries one; no other kind does.
 */
export type TextKind =
  | 'person'
  | 'place'
  | 'role'
  | 'relationship'
  | 'carrier'
  | 'value'
  | 'medication'
  | 'medication_reason'
  | 'diagnosis'
  | 'appointment_purpose'
  | 'allergy'
  | 'note'
  | 'address_text'
  | 'phone'
  | 'code';

export interface PayloadFieldSpec {
  kind: PayloadFieldKind;
  /** How the value is cased and shown; every string field has one. */
  textKind?: TextKind;
  /** Required = must be present and non-null. */
  required?: boolean;
  /** kind='enum' only: the closed value set, what a person may pick. */
  values?: readonly string[];
  /** kind='enum' only: the values the note reader may say, when fewer
   *  than a person may pick (the server copy's prompt and reader check).
   *  Absent = values. */
  readerValues?: readonly string[];
  /** Confirm-time-only field (CM edits / directory links); not a card input. */
  confirmOnly?: boolean;
  /**
   * Display-only plumbing: shown in the summary line + carried on the wire,
   * but never rendered as an editable form input (date_text — the note's own
   * honest-null date phrasing; the CM never types it). FE-render-only; the BE
   * mirrors it for seam symmetry (prompt/zod/projection ignore it).
   */
  displayOnly?: boolean;
  /**
   * Model-facing single-assertion field with no projection column of its
   * own (code_status_value): the prompt and the confirm card keep it (a
   * note asserts ONE change), the writer MERGES it into the projection's
   * set, and DIRECT entry excludes it (humans compose the set instead —
   * spec §B3, WS-3).
   */
  mergeOnly?: boolean;
  /**
   * Direct-entry-only field (code_status_values): excluded from the
   * generated prompt AND the confirm card (the model asserts single
   * values; the CM's record widget composes the whole set). The zod
   * schema accepts it so a direct payload validates; the writer writes
   * the set verbatim after exclusivity validation.
   */
  directOnly?: boolean;
  /**
   * Set by a person, never by the note reader (an appointment's time):
   * excluded from the generated prompt only. The record dialog and the
   * confirm card show it as a box, and the schemas accept it.
   */
  notFromNotes?: boolean;
  /**
   * enum_set only: an emptied set is stored as NULL (nothing recorded).
   */
  clearable?: boolean;
  /**
   * Directory-derived field (provider specialty/organization): on the
   * record read it renders from the LINKED Directory entry exclusively —
   * it has no per-client projection column and the projection writer
   * never persists it. The prompt still asks for it (the extracted text
   * helps the CM pick the right Directory link), and a proposed card's
   * confirm event keeps what the note said; but no card, form or fact
   * shows it, because nothing stores it per client. The DIRECT edit/add
   * allowlists exclude it too: accepting it there would be a silent
   * no-op write (WS-5).
   */
  directoryOnly?: boolean;
  /**
   * The record dialog draws the field as a box of many lines that grows
   * with its text (a typed address, a clinical note). Carried on both
   * sides for FE↔BE mirror symmetry (the conformance seam pins spec
   * values); the prompt/zod/projection ignore it.
   */
  multiline?: boolean;
  /**
   * The value a new row takes when none is given. The record dialog
   * opens on it and sends it; the field is never emptied. Set only on
   * an enum whose column is NOT NULL with a server default of the same
   * value.
   */
  defaultValue?: string;
  /**
   * The field applies only while another field's value is one of
   * `values` (the code-status fields, with the instrument). Where it
   * does not apply it is not offered, shown or sent. Read it through
   * fieldApplies, never by field name; the prompt and the zod schema
   * ignore it.
   */
  appliesWhen?: { field: string; values: readonly string[] };
  /** One-line hint (prompt + field help). */
  hint?: string;
}

export type ProposalPayloadSpec = Readonly<Record<string, PayloadFieldSpec>>;

/**
 * True when a field applies among the values `read` gives: always for a
 * field without appliesWhen, else when the named field's value, as
 * text, is one of the rule's values.
 */
export function fieldApplies(spec: PayloadFieldSpec, read: (field: string) => unknown): boolean {
  const rule = spec.appliesWhen;
  if (rule === undefined) return true;
  return rule.values.includes(String(read(rule.field)));
}

/**
 * True when an edit may empty the field (send null): an optional field
 * with no default, of a text, email, date or single-choice kind, or a
 * set marked clearable. A required field, a defaulted field, a flag, any
 * other set and a link id are never emptied.
 */
export function isClearable(spec: PayloadFieldSpec): boolean {
  return (
    spec.required !== true &&
    spec.defaultValue === undefined &&
    (spec.kind === 'string' ||
      spec.kind === 'email' ||
      spec.kind === 'date' ||
      spec.kind === 'time' ||
      spec.kind === 'enum' ||
      spec.kind === 'postal_address' ||
      (spec.kind === 'enum_set' && spec.clearable === true))
  );
}

const MEDICATION_SPEC: ProposalPayloadSpec = {
  name: { kind: 'string', textKind: 'medication', required: true, hint: 'the medication name exactly as written' },
  dose: { kind: 'string', textKind: 'medication', hint: 'dose as text, e.g. "5 mg"; never guess' },
  schedule: { kind: 'string', textKind: 'medication', hint: 'schedule/frequency as text, e.g. "evenings"' },
  times_of_day: {
    kind: 'enum_set',
    values: MEDICATION_TIMES_OF_DAY,
    clearable: true,
    hint: 'the times the note STATES: morning (AM, breakfast, on waking), afternoon (noon, lunch), evening (PM, dinner, supper), bedtime (at night, nightly, before bed, HS), as_needed (PRN, as needed). Stated times always win. With no stated time, a count maps as the medication list does: once daily -> ["morning"], twice daily/BID -> ["morning","evening"], three times daily/TID -> ["morning","afternoon","evening"], four times daily/QID -> ["morning","afternoon","evening","bedtime"]. Any other timing (every other day, weekly, none given) -> null. Never [] and never a guess',
  },
  prescriber: { kind: 'string', textKind: 'person', hint: 'prescriber name as written in the note, if stated' },
  reason: { kind: 'string', textKind: 'medication_reason', hint: 'stated reason for the add/change/stop, if any' },
  status: { kind: 'enum', values: MEDICATION_STATUSES, defaultValue: 'active' },
  started_on: { kind: 'date' },
  ended_on: { kind: 'date' },
  prescriber_provider_id: { kind: 'uuid', confirmOnly: true },
  prescriber_directory_individual_id: { kind: 'uuid', confirmOnly: true },
  prescriber_directory_org_id: { kind: 'uuid', confirmOnly: true },
};

const APPOINTMENT_SPEC: ProposalPayloadSpec = {
  provider: {
    kind: 'string',
    textKind: 'person',
    required: true,
    hint: 'provider name or specialty as written, e.g. "Dr. Chen" or "cardiology"',
  },
  status: { kind: 'enum', values: APPOINTMENT_STATUSES, defaultValue: 'scheduled' },
  scheduled_for: { kind: 'date', hint: 'ISO date if the note states one; else null' },
  // 0218: the time beside the date. The note reader proposes it (v12) only
  // when the note states a clock time; a person sets or corrects it on the
  // record and in the confirm queue.
  scheduled_time: {
    kind: 'time',
    hint: 'only when the note states a clock time for this appointment ("2:30", "10am", "at 14:00"); never from "morning", "afternoon" or "after lunch"; else null',
  },
  occurred_on: { kind: 'date', hint: 'ISO date if the note states one; else null' },
  date_text: {
    kind: 'string',
    textKind: 'value',
    displayOnly: true,
    hint: 'the note\'s own date phrasing when no ISO date is stated, e.g. "in a few weeks"',
  },
  purpose: { kind: 'string', textKind: 'appointment_purpose' },
  // The record forms draw it as Notes, a box of many lines; the reader's
  // hint (the prompt shows the hint, never multiline) is unchanged.
  outcome: { kind: 'string', textKind: 'value', multiline: true, hint: 'past appointments only; one line' },
  // 0144 (Soak #2 Wave B / G2): recurrence (a controlled cadence) + the
  // place the appointment happens. One row represents a recurring series.
  recurrence: { kind: 'enum', values: APPOINTMENT_RECURRENCES, defaultValue: 'none' },
  // The note's words for the place: the reader proposes it as written,
  // and the card shows it as "From the note"; never a box of its own
  // (0233: the place is the name and the address below).
  location: {
    kind: 'string',
    textKind: 'address_text',
    displayOnly: true,
    hint: 'where it happens, e.g. "DaVita Dialysis, 12 Main St", if stated',
  },
  // 0233: the place, set by a person on the form or the card.
  location_name: { kind: 'string', textKind: 'place', notFromNotes: true },
  location_address: { kind: 'postal_address', notFromNotes: true },
  // Where a Directory pick came from (the name and address were copied).
  location_directory_org_id: { kind: 'uuid', confirmOnly: true },
  location_directory_individual_id: { kind: 'uuid', confirmOnly: true },
  provider_id: { kind: 'uuid', confirmOnly: true },
  // The record page's Directory pick for the provider (a person or an
  // organization): resolved to a care-team row before the writer, as a
  // medication's prescriber pick is.
  provider_directory_individual_id: { kind: 'uuid', confirmOnly: true },
  provider_directory_org_id: { kind: 'uuid', confirmOnly: true },
};

const CONDITION_SPEC: ProposalPayloadSpec = {
  name: { kind: 'string', textKind: 'diagnosis', required: true, hint: 'the condition/diagnosis name' },
  status: {
    kind: 'enum',
    values: CONDITION_STATUSES,
    defaultValue: 'active',
    hint: 'as stated: "active" a current diagnosis; "watch" suspected, being worked up, or being monitored; "resolved" stated resolved',
  },
  noted_on: { kind: 'date', hint: 'ISO date ONLY when the note states when it was diagnosed or first noted; else null. A condition with no stated date is still proposed' },
  note: { kind: 'string', textKind: 'note', multiline: true, hint: 'one-line context, if any' },
};

const HOUSING_SPEC: ProposalPayloadSpec = {
  living_situation: { kind: 'enum', values: LIVING_SITUATIONS, required: true },
  // 0148 (Soak #2 Wave B / D9): renamed detail -> note to match the
  // note's own words, shown on the confirm card and not stored (22l1).
  // Mirrors api/_shared/noteVocabulary.ts.
  note: { kind: 'string', textKind: 'value', hint: 'facility/place name or context as written' },
  // 22l2: where they now live or stay, set on the card; never asked of
  // the reader. Mirrors api/_shared/noteVocabulary.ts.
  address: { kind: 'postal_address', notFromNotes: true },
  effective_on: { kind: 'date' },
};

const RISK_SPEC: ProposalPayloadSpec = {
  category: { kind: 'risk_category', required: true },
  level: { kind: 'enum', values: RISK_LEVELS, required: true },
  basis: { kind: 'string', textKind: 'note', multiline: true, hint: 'one line: what in the note establishes this' },
};

const PROVIDER_SPEC: ProposalPayloadSpec = {
  display_name: { kind: 'string', textKind: 'person', required: true, hint: 'the provider\'s name as written' },
  role: { kind: 'string', textKind: 'role', hint: 'their role in this client\'s care, if stated' },
  specialty: { kind: 'string', textKind: 'value', directoryOnly: true },
  organization: { kind: 'string', textKind: 'place', directoryOnly: true },
  // 0140 (WS-5): per-client office contact columns. A set value
  // overrides; the linked Directory entry's phone/address display as
  // the fallback.
  office_phone: { kind: 'string', textKind: 'phone', hint: 'their office phone number, if stated' },
  // 0234: the five parts on the forms and the card. The reader still
  // takes it as the note's text (renderField's postal_address rule), so
  // the prompt reads as before.
  office_address: { kind: 'postal_address', hint: 'their office address, if stated' },
  // 0231: the provider's own email. Set by a person, never proposed by a
  // reader, so the prompt holds still; NULL shows the Directory's.
  email: { kind: 'email', notFromNotes: true },
  directory_individual_id: { kind: 'uuid', confirmOnly: true },
  directory_org_id: { kind: 'uuid', confirmOnly: true },
};

const CONTACT_SPEC: ProposalPayloadSpec = {
  name: { kind: 'string', textKind: 'person', required: true, hint: 'the contact\'s name' },
  relationship: { kind: 'string', textKind: 'relationship', hint: 'e.g. "daughter", "attorney"' },
  phone: { kind: 'string', textKind: 'phone' },
  email: { kind: 'email' },
  address: { kind: 'postal_address', hint: 'the contact\'s mailing/street address, ONLY if stated' },
  is_poa: { kind: 'boolean' },
  is_hcp: { kind: 'boolean' },
  // The client's legal guardian (0150) — a first-class relationship flag,
  // like POA/HCP; distinct from the Legal & wishes guardianship instrument
  // (whose designee is a free-text name, not a person link).
  is_guardian: { kind: 'boolean' },
  is_emergency: { kind: 'boolean' },
  lives_with: { kind: 'boolean' },
  // Primary Billing Contact (0137, WS-4 D4) — who in the family handles
  // billing conversations; distinct from the invoice bill-to (0095).
  is_billing: { kind: 'boolean' },
  // The Directory entry the contact is (0227), picked in the contact
  // form; never asked of the note reader.
  directory_individual_id: { kind: 'uuid', confirmOnly: true },
  directory_org_id: { kind: 'uuid', confirmOnly: true },
};

const HOSPITAL_SPEC: ProposalPayloadSpec = {
  kind: { kind: 'enum', values: HOSPITAL_KINDS, required: true },
  facility: { kind: 'string', textKind: 'place', hint: 'facility name as written; null if not named' },
  event_on: { kind: 'date' },
  date_text: {
    kind: 'string',
    textKind: 'value',
    displayOnly: true,
    hint: 'the note\'s date phrasing when no ISO date is stated',
  },
  note: { kind: 'string', textKind: 'note', multiline: true, hint: 'one-line outcome, e.g. "discharged same day"' },
  facility_org_id: { kind: 'uuid', confirmOnly: true },
};

const DIRECTIVE_SPEC: ProposalPayloadSpec = {
  instrument: { kind: 'enum', values: LEGAL_INSTRUMENTS, required: true },
  status: { kind: 'enum', values: LEGAL_STATUSES, required: true },
  // The model's single assertion ("now DNR") — merged into the record's
  // set by the writer (spec §B3); never a direct-entry input. Both
  // code-status fields apply only while the instrument carries one.
  code_status_value: {
    kind: 'enum',
    values: CODE_STATUS_VALUES,
    mergeOnly: true,
    appliesWhen: { field: 'instrument', values: CODE_STATUS_INSTRUMENTS },
    hint: 'ONLY for instrument code_status or polst; null otherwise',
  },
  // The composed set (direct entry — the directive card's multi-select).
  // dnr/dni/dnh combine; full/comfort/unknown are exclusive singletons.
  code_status_values: {
    kind: 'enum_set',
    values: CODE_STATUS_VALUES,
    directOnly: true,
    appliesWhen: { field: 'instrument', values: CODE_STATUS_INSTRUMENTS },
  },
  designee: { kind: 'string', textKind: 'person', hint: 'named agent/proxy, if stated' },
  effective_on: { kind: 'date' },
};

const COVERAGE_SPEC: ProposalPayloadSpec = {
  program: { kind: 'enum', values: COVERAGE_PROGRAMS, readerValues: COVERAGE_READER_PROGRAMS, required: true },
  provider: {
    kind: 'string',
    textKind: 'carrier',
    hint: 'the insurance carrier / plan name as written, e.g. "United Healthcare Oxford", "Aetna"',
  },
  status: { kind: 'enum', values: COVERAGE_STATUSES, defaultValue: 'unverified' },
  verified_on: { kind: 'date' },
  detail: { kind: 'string', textKind: 'note', multiline: true, hint: 'plan/policy context as written, if any' },
  // The card's fields (0213). Direct entry only: the note prompt and the
  // confirm card leave them out, and a note confirm never writes them.
  plan_name: { kind: 'string', textKind: 'carrier', directOnly: true },
  member_id: { kind: 'string', textKind: 'code', directOnly: true },
  group_number: { kind: 'string', textKind: 'code', directOnly: true },
  card_phone: { kind: 'string', textKind: 'phone', directOnly: true },
  rx_bin: { kind: 'string', textKind: 'code', directOnly: true },
  rx_pcn: { kind: 'string', textKind: 'code', directOnly: true },
  rx_group: { kind: 'string', textKind: 'code', directOnly: true },
  holder_name: { kind: 'string', textKind: 'person', directOnly: true },
  holder_relationship: { kind: 'string', textKind: 'relationship', directOnly: true },
};

const FUNCTIONAL_STATUS_SPEC: ProposalPayloadSpec = {
  activity: { kind: 'enum', values: FUNCTIONAL_ACTIVITIES, required: true },
  level: {
    kind: 'enum',
    values: FUNCTIONAL_LEVELS,
    required: true,
    hint: 'the STATED capability level; "not_assessed" when explicitly not addressed; never infer',
  },
  note: {
    kind: 'string',
    textKind: 'note',
    multiline: true,
    hint: 'one line: what in the note establishes this, e.g. "needs standby assist"',
  },
};

const ALLERGY_SPEC: ProposalPayloadSpec = {
  substance: {
    kind: 'string',
    textKind: 'allergy',
    required: true,
    hint: 'the allergen exactly as written, e.g. "penicillin"; never guess spellings',
  },
  reaction: { kind: 'string', textKind: 'value', hint: 'the stated reaction, if any, e.g. "hives"' },
  severity: {
    kind: 'enum',
    values: ALLERGY_SEVERITIES,
    hint: 'ONLY when the note states it; null otherwise; never infer severity',
  },
};

/**
 * The pinned per-type payload shapes — §1 columns as canonical JSON field
 * names. The medication family shares one spec (the action rides the
 * proposal_type); ditto appointments and conditions.
 */
export const PROPOSAL_PAYLOAD_SPECS: Readonly<Record<NoteProposalType, ProposalPayloadSpec>> = {
  medication_add: MEDICATION_SPEC,
  medication_change: MEDICATION_SPEC,
  medication_discontinue: MEDICATION_SPEC,
  appointment_past: APPOINTMENT_SPEC,
  appointment_upcoming: APPOINTMENT_SPEC,
  condition_add: CONDITION_SPEC,
  condition_change: CONDITION_SPEC,
  housing_change: HOUSING_SPEC,
  risk_change: RISK_SPEC,
  provider_new: PROVIDER_SPEC,
  contact_change: CONTACT_SPEC,
  hospital_event: HOSPITAL_SPEC,
  directive_change: DIRECTIVE_SPEC,
  coverage_change: COVERAGE_SPEC,
  functional_status_change: FUNCTIONAL_STATUS_SPEC,
  allergy_change: ALLERGY_SPEC,
};

// ---------------------------------------------------------------------
// Rendering helpers (frontend-only; derived FROM the registry so the
// card fan-out never re-lists field names by hand).
// ---------------------------------------------------------------------

/** Spec lookup for a wire proposal_type (loose string), or undefined. */
export function specForType(type: string): ProposalPayloadSpec | undefined {
  return (PROPOSAL_PAYLOAD_SPECS as Record<string, ProposalPayloadSpec>)[type];
}

/**
 * The fields a confirm card reads, edits and lists as facts, in registry
 * order: every field EXCEPT the confirm-time-only directory-link uuids
 * (those are resolved by the CM against the Directory, not typed into the
 * card) and the directory-derived fields (provider specialty and
 * organization: nothing stores them per client, so the card offers
 * neither a box nor a fact for them; the note's own words still ride the
 * confirm payload untouched).
 */
export function editableFields(spec: ProposalPayloadSpec): [string, PayloadFieldSpec][] {
  return Object.entries(spec).filter(([, f]) => !f.confirmOnly && f.kind !== 'uuid' && !f.directoryOnly);
}

/**
 * The fields a confirm card renders as EDITABLE INPUTS: editableFields minus
 * display-only plumbing (date_text) minus direct-entry-only fields
 * (code_status_values — the model asserts single values; the CM's record
 * widget composes the set). The summary line + the confirm wire keep
 * reading the full editableFields set, so the value still shows and still
 * rides the payload untouched; it just isn't a typed-in form field. A
 * directory-derived field is already out of editableFields, so it is
 * neither a box nor a fact here.
 */
export function formFields(spec: ProposalPayloadSpec): [string, PayloadFieldSpec][] {
  return editableFields(spec).filter(([, f]) => !f.displayOnly && !f.directOnly);
}

/**
 * The fields the RECORD dialog (direct entry/edit) renders as inputs:
 * editableFields minus display-only plumbing minus merge-only fields
 * (code_status_value — a model-assertion field; direct entry composes
 * the whole set through its enum_set sibling instead) minus
 * directory-derived fields (provider specialty/organization — no
 * per-client column; the API refuses them on the direct routes, WS-5).
 */
export function directFormFields(spec: ProposalPayloadSpec): [string, PayloadFieldSpec][] {
  return editableFields(spec).filter(([, f]) => !f.displayOnly && !f.mergeOnly && !f.directoryOnly);
}
